<template>
    <div class="login-container">
        <!-- Language switcher -->
        <div class="lang-switch">
            <button :class="['lang-btn', { active: $i18n.locale === 'sr' }]" @click="setLang('sr')">🇷🇸 SR</button>
            <button :class="['lang-btn', { active: $i18n.locale === 'en' }]" @click="setLang('en')">🇬🇧 EN</button>
        </div>

        <div class="login-wrapper">
            <div class="login-card">
                <!-- Header -->
                <div class="login-header">
                    <div class="logo-container">
                        <i class="tim-icons icon-cloud-upload-94"></i>
                    </div>
                    <h1 class="login-title">Fiscal Cloud</h1>
                    <p class="login-subtitle">{{ isLogin ? $t('login.title') : $t('login.register') }}</p>
                </div>

                <!-- Tab Toggle -->
                <div class="tab-toggle">
                    <button :class="['toggle-btn', { active: isLogin }]" @click="isLogin = true">
                        {{ $t('login.login') }}
                    </button>
                    <button :class="['toggle-btn', { active: !isLogin }]" @click="isLogin = false">
                        {{ $t('login.register') }}
                    </button>
                </div>

                <!-- Login Form -->
                <div class="login-body" v-if="isLogin">
                    <div class="form-group">
                        <label class="form-label">{{ $t('login.username') }}</label>
                        <base-input v-model="loginForm.username" type="text"
                            :placeholder="$t('login.username')"
                            addon-left-icon="tim-icons icon-single-02"
                            @keyup.enter.native="login">
                        </base-input>
                    </div>

                    <div class="form-group">
                        <label class="form-label">{{ $t('login.password') }}</label>
                        <base-input v-model="loginForm.password" type="password"
                            :placeholder="$t('login.password')"
                            addon-left-icon="tim-icons icon-lock-circle"
                            @keyup.enter.native="login">
                        </base-input>
                    </div>

                    <base-button type="primary" class="login-button" size="lg" block
                        @click="login" :disabled="loading">
                        <i class="tim-icons icon-check-2" v-if="!loading"></i>
                        <i class="tim-icons icon-refresh-02 spin" v-else></i>
                        {{ loading ? $t('common.loading') : $t('login.login') }}
                    </base-button>
                </div>

                <!-- Register Form -->
                <div class="login-body" v-else>
                    <div class="section-label">{{ $t('login.companyName') }}</div>

                    <div class="form-row">
                        <div class="form-group half">
                            <label class="form-label">{{ $t('login.companyName') }} *</label>
                            <base-input v-model="registerForm.companyName" type="text"
                                placeholder="Moja firma doo"
                                addon-left-icon="tim-icons icon-bank">
                            </base-input>
                        </div>
                        <div class="form-group half">
                            <label class="form-label">{{ $t('login.tin') }}</label>
                            <base-input v-model="registerForm.tin" type="text"
                                placeholder="123456789"
                                addon-left-icon="tim-icons icon-badge">
                            </base-input>
                        </div>
                    </div>

                    <div class="section-label">{{ $t('login.name') }}</div>

                    <div class="form-row">
                        <div class="form-group half">
                            <label class="form-label">{{ $t('login.username') }} *</label>
                            <base-input v-model="registerForm.username" type="text"
                                :placeholder="$t('login.username')"
                                addon-left-icon="tim-icons icon-single-02">
                            </base-input>
                        </div>
                        <div class="form-group half">
                            <label class="form-label">{{ $t('login.name') }} *</label>
                            <base-input v-model="registerForm.name" type="text"
                                :placeholder="$t('login.name')"
                                addon-left-icon="tim-icons icon-badge">
                            </base-input>
                        </div>
                    </div>

                    <div class="form-group">
                        <label class="form-label">{{ $t('login.email') }}</label>
                        <base-input v-model="registerForm.email" type="email"
                            placeholder="primer@email.com"
                            addon-left-icon="tim-icons icon-email-85">
                        </base-input>
                    </div>

                    <div class="form-row">
                        <div class="form-group half">
                            <label class="form-label">{{ $t('login.password') }} * <small>(min. 6)</small></label>
                            <base-input v-model="registerForm.password" type="password"
                                :placeholder="$t('login.password')"
                                addon-left-icon="tim-icons icon-lock-circle">
                            </base-input>
                        </div>
                        <div class="form-group half">
                            <label class="form-label">{{ $t('login.password') }} (confirm) *</label>
                            <base-input v-model="registerForm.confirmPassword" type="password"
                                :placeholder="$t('login.password')"
                                addon-left-icon="tim-icons icon-lock-circle"
                                @keyup.enter.native="register">
                            </base-input>
                        </div>
                    </div>

                    <base-button type="primary" class="login-button" size="lg" block
                        @click="register" :disabled="loading">
                        <i class="tim-icons icon-simple-add" v-if="!loading"></i>
                        <i class="tim-icons icon-refresh-02 spin" v-else></i>
                        {{ loading ? $t('common.loading') : $t('login.register') }}
                    </base-button>
                </div>

                <!-- Footer -->
                <div class="login-footer">
                    <p class="footer-text">Fiscal Cloud Service v1.0</p>
                </div>
            </div>
        </div>

        <!-- Error Notification -->
        <transition name="notif-fade">
            <div v-if="errorMessage" class="error-toast" @click="errorMessage = ''">
                <i class="tim-icons icon-alert-circle-exc"></i>
                <span>{{ errorMessage }}</span>
            </div>
        </transition>

        <!-- Success Notification -->
        <transition name="notif-fade">
            <div v-if="successMessage" class="success-toast" @click="successMessage = ''">
                <i class="tim-icons icon-check-2"></i>
                <span>{{ successMessage }}</span>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from '@/util/axiosinstance'

export default {
    name: 'Login',
    data() {
        return {
            isLogin: true,
            loading: false,
            errorMessage: '',
            successMessage: '',
            loginForm: {
                username: '',
                password: ''
            },
            registerForm: {
                companyName: '',
                tin: '',
                username: '',
                name: '',
                email: '',
                password: '',
                confirmPassword: ''
            }
        }
    },
    methods: {
        setLang(lang) {
            this.$i18n.locale = lang
            localStorage.setItem('fiscal_lang', lang)
        },
        login() {
            if (!this.loginForm.username || !this.loginForm.password) {
                this.showError(this.$t('login.username') + ' & ' + this.$t('login.password'))
                return
            }

            this.loading = true
            axios.post('/auth/login', this.loginForm).then(result => {
                this.loading = false
                if (result.data.status === 'OK') {
                    localStorage.setItem('fiscal_api_key', result.data.apiKey)
                    localStorage.setItem('fiscal_user', JSON.stringify({
                        name: result.data.name,
                        userId: result.data.userId,
                        role: result.data.role,
                        clientId: result.data.clientId,
                        clientName: result.data.clientName,
                        subscriptionStatus: result.data.subscriptionStatus,
                        isSubscriptionActive: result.data.isSubscriptionActive,
                        trialDaysRemaining: result.data.trialDaysRemaining
                    }))
                    // SUPERADMIN ide na admin dashboard
                    if (result.data.role === 'SUPERADMIN') {
                        this.$router.push('/admin/dashboard')
                    } else {
                        this.$router.push('/fiscal/locations')
                    }
                } else {
                    this.showError(result.data.error || 'Login failed')
                }
            }).catch(error => {
                this.loading = false
                if (error.response && error.response.data) {
                    this.showError(error.response.data.error || 'Login error')
                } else {
                    this.showError('Connection error')
                }
            })
        },

        register() {
            if (!this.registerForm.companyName || !this.registerForm.companyName.trim()) {
                this.showError(this.$t('login.companyName') + ' required')
                return
            }
            if (!this.registerForm.username || !this.registerForm.name) {
                this.showError('Popunite sva obavezna polja')
                return
            }
            if (this.registerForm.password.length < 6) {
                this.showError(this.$t('login.password') + ' min 6')
                return
            }
            if (this.registerForm.password !== this.registerForm.confirmPassword) {
                this.showError('Passwords do not match')
                return
            }

            this.loading = true
            axios.post('/auth/register', {
                companyName: this.registerForm.companyName,
                tin: this.registerForm.tin,
                username: this.registerForm.username,
                name: this.registerForm.name,
                email: this.registerForm.email,
                password: this.registerForm.password
            }).then(result => {
                this.loading = false
                if (result.data.status === 'OK') {
                    this.showSuccess('OK')
                    this.isLogin = true
                    this.loginForm.username = this.registerForm.username
                    this.registerForm = { companyName: '', tin: '', username: '', name: '', email: '', password: '', confirmPassword: '' }
                } else {
                    this.showError(result.data.error || 'Registration error')
                }
            }).catch(error => {
                this.loading = false
                if (error.response && error.response.data) {
                    this.showError(error.response.data.error || 'Registration error')
                } else {
                    this.showError('Connection error')
                }
            })
        },

        showError(msg) {
            this.errorMessage = msg
            setTimeout(() => { this.errorMessage = '' }, 4000)
        },

        showSuccess(msg) {
            this.successMessage = msg
            setTimeout(() => { this.successMessage = '' }, 4000)
        }
    },
    created() {
        if (localStorage.getItem('fiscal_api_key')) {
            const user = JSON.parse(localStorage.getItem('fiscal_user') || '{}')
            if (user.role === 'SUPERADMIN') {
                this.$router.push('/admin/dashboard')
            } else {
                this.$router.push('/fiscal/locations')
            }
        }
    }
}
</script>

<style scoped>
.lang-switch {
    position: fixed;
    top: 15px;
    right: 20px;
    z-index: 100000;
    display: flex;
    gap: 4px;
    background: rgba(255,255,255,0.15);
    border-radius: 8px;
    padding: 3px;
    backdrop-filter: blur(10px);
}
.lang-btn {
    padding: 6px 12px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: rgba(255,255,255,0.7);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    letter-spacing: 0.5px;
}
.lang-btn:hover {
    color: #fff;
    background: rgba(255,255,255,0.1);
}
.lang-btn.active {
    background: rgba(255,255,255,0.25);
    color: #fff;
}

.login-container {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    z-index: 99999;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%);
    padding: 20px;
    overflow: auto;
}
.login-container::before {
    content: '';
    position: absolute;
    top: -50%; left: -50%;
    width: 200%; height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px);
    background-size: 40px 40px;
    animation: moveBackground 30s linear infinite;
}
@keyframes moveBackground {
    0% { transform: translate(0, 0); }
    100% { transform: translate(40px, 40px); }
}
.login-wrapper {
    width: 100%;
    max-width: 520px;
    position: relative;
    z-index: 1;
}
.login-card {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    animation: slideUp 0.4s ease;
}
@keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
}
.login-header {
    background: linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%);
    padding: 35px 30px 25px;
    text-align: center;
}
.logo-container {
    width: 70px; height: 70px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 15px;
    border: 2px solid rgba(255, 255, 255, 0.3);
}
.logo-container i { font-size: 32px; color: white; }
.login-title {
    color: white; font-size: 28px;
    font-weight: 700; margin: 0 0 6px 0;
}
.login-subtitle {
    color: rgba(255, 255, 255, 0.85);
    font-size: 14px; margin: 0;
}
.tab-toggle { display: flex; background: #f1f3f5; }
.toggle-btn {
    flex: 1; padding: 12px; border: none;
    background: transparent; font-size: 14px;
    font-weight: 600; color: #6c757d;
    cursor: pointer; transition: all 0.3s ease;
    border-bottom: 3px solid transparent;
}
.toggle-btn.active {
    color: #1a73e8; background: #ffffff;
    border-bottom-color: #1a73e8;
}
.toggle-btn:hover:not(.active) { color: #495057; background: #e9ecef; }
.login-body { padding: 30px; }
.section-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #1a73e8;
    margin-bottom: 12px;
    padding-bottom: 6px;
    border-bottom: 2px solid #e7f3ff;
}
.form-group { margin-bottom: 16px; }
.form-row { display: flex; gap: 15px; }
.form-group.half { flex: 1; }
.form-label {
    display: block; font-size: 13px;
    font-weight: 600; color: #2c3e50;
    margin-bottom: 6px;
}
.form-label small { font-weight: 400; color: #6c757d; }
.login-button {
    margin-top: 10px;
    padding: 12px 30px !important;
    font-size: 16px !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    letter-spacing: 0.5px;
    transition: all 0.3s ease !important;
}
.login-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(26, 115, 232, 0.4) !important;
}
.login-button i { margin-right: 8px; }
.login-footer {
    background: #f8f9fa; padding: 15px;
    text-align: center; border-top: 1px solid #e9ecef;
}
.footer-text { color: #6c757d; font-size: 12px; margin: 0; }
.error-toast, .success-toast {
    position: fixed; top: 30px; right: 30px;
    padding: 14px 20px; border-radius: 10px;
    display: flex; align-items: center; gap: 10px;
    font-size: 14px; font-weight: 500;
    z-index: 100000; cursor: pointer;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
    animation: slideIn 0.3s ease;
}
.error-toast { background: #dc3545; color: white; }
.success-toast { background: #28a745; color: white; }
@keyframes slideIn {
    from { transform: translateX(100px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
}
.notif-fade-enter-active, .notif-fade-leave-active { transition: all 0.3s ease; }
.notif-fade-enter, .notif-fade-leave-to { opacity: 0; transform: translateX(50px); }
.spin { animation: spin 1s linear infinite; }
@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
::v-deep .form-control {
    border: 2px solid #e9ecef; border-radius: 8px;
    padding: 10px 14px; font-size: 14px; color: #2c3e50;
}
::v-deep .form-control:focus {
    border-color: #1a73e8;
    box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.1);
}
::v-deep .input-group-text {
    background: #f8f9fa; border: 2px solid #e9ecef;
    border-right: none; border-radius: 8px 0 0 8px;
    padding: 0 12px; min-width: 42px;
    display: flex; align-items: center; justify-content: center;
}
::v-deep .input-group-text i { font-size: 15px; color: #6c757d; }
::v-deep .form-control { border-left: none; border-radius: 0 8px 8px 0; }
@media (max-width: 576px) {
    .form-row { flex-direction: column; gap: 0; }
    .login-header { padding: 25px 20px 20px; }
    .login-body { padding: 25px 20px; }
    .login-title { font-size: 24px; }
}
</style>